import { Button, Flex, Modal, Select, Space } from "antd"
import { FC, useEffect, useState, useMemo } from "react"
import ButtonAudience from "./ButtonAudience"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { IAudienceOption } from "../types/audience"
import { IPlacementDataForUpdate } from "../store/types/placements"
import useAudience from "../hooks/useAudience"

interface ISelectAudienceProps {
  audienceId?: string
  campaignId: number
  setPlacementsDataForUpdate: React.Dispatch<React.SetStateAction<IPlacementDataForUpdate[]>>
}

const DEFAULT_AUDIENCE_ID = "Select Audience"

const SelectAudience: FC<ISelectAudienceProps> = ({
  audienceId,
  campaignId,
  setPlacementsDataForUpdate,
}) => {
  const [value, setValue] = useState<string | null>(audienceId || null)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [prevSelected, setPrevSelected] = useState<string | null>(audienceId || null)

  const { applyAudiencesToSelectedCampaigns } = useAudience({
    setPlacementsDataForUpdate,
    selectedCampaignIds: [campaignId],
    selectedAudience: value,
  })

  const { audiences, loaded } = useTypedSelector((state) => state.audiences)

  useEffect(() => {
    setValue(audienceId || null)
  }, [audienceId])

  const options = useMemo(
    () =>
      audiences.map((audience) => ({
        label: audience.audienceResolved,
        tooltip: audience.audienceTooltip,
        value: audience.audienceId,
      })),
    [audiences]
  )

  function handleChange(value: string) {
    setValue(value)
  }

  function handleOk() {
    const applied = applyAudiencesToSelectedCampaigns()
    if (!applied) {
      return
    }

    setPrevSelected(value)
    setIsModalOpen(false)
  }

  function handleCancel() {
    setValue(prevSelected)
    setIsModalOpen(false)
  }

  function handleRestoreDefaults() {
    setValue(audienceId || null)
  }

  return (
    <Space>
      <ButtonAudience
        options={options}
        setIsModalOpen={setIsModalOpen}
        value={value || DEFAULT_AUDIENCE_ID}
      />

      <Modal
        title="Select Audience"
        closable={{ "aria-label": "Custom Close Button" }}
        open={isModalOpen}
        width={"60%"}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <Flex justify="space-between" gap={10}>
          <Select
            options={options}
            value={value}
            onChange={handleChange}
            style={{ width: "100%" }}
            placeholder={DEFAULT_AUDIENCE_ID}
            loading={loaded}
            disabled={loaded}
          />
          <Button onClick={handleRestoreDefaults}>Restore default</Button>
        </Flex>
      </Modal>
    </Space>
  )
}

export default SelectAudience
